<template>
  <!-- Modal de connexion -->
  <div v-if="showLoginModal" class="modal-overlay" @click.self="closeModals">
    <div class="modal-content">
      <button class="close-button" @click="closeModals">×</button>
      <h2>Connexion</h2>
      
      <form @submit.prevent="handleLogin">
        <div class="form-group">
          <input 
            type="text" 
            placeholder="Email ou numéro de téléphone" 
            v-model="loginIdentifier" 
            required
          >
        </div>
        
        <div class="form-group">
          <input 
            type="password" 
            placeholder="Mot de passe" 
            v-model="loginPassword" 
            required
          >
        </div>

        <div class="forgot-link">
          <router-link to="/forgot-password" @click="closeModals">
            Mot de passe oublié ?
          </router-link>
        </div>

        <button type="submit" class="submit-button" :disabled="loading">
          {{ loading ? 'Connexion...' : 'Se connecter' }}
        </button>
      </form>
      
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
      
      <p class="auth-switch">
        Pas encore de compte ? 
        <a href="#" @click.prevent="switchToRegister">S'inscrire</a>
      </p>
    </div>
  </div>

  <!-- Modal d'inscription en 2 étapes -->
  <div v-if="showRegisterModal" class="modal-overlay" @click.self="closeModals">
    <div class="modal-content register-modal">
      <button class="close-button" @click="closeModals">×</button>
      
      <!-- Indicateur d'étape -->
      <div class="step-indicator">
        <div class="step" :class="{ active: registerStep === 1, completed: registerStep > 1 }">
          <span class="step-number">1</span>
          <span class="step-label">Identifiants</span>
        </div>
        <div class="step-line" :class="{ active: registerStep > 1 }"></div>
        <div class="step" :class="{ active: registerStep === 2 }">
          <span class="step-number">2</span>
          <span class="step-label">Coordonnées</span>
        </div>
      </div>

      <h2>{{ registerStep === 1 ? 'Créer votre compte' : 'Informations de livraison' }}</h2>
      <p class="step-subtitle" v-if="registerStep === 1">
        Commencez par choisir vos identifiants de connexion
      </p>
      <p class="step-subtitle" v-else>
        Ces informations vous seront utiles pour vos futures commandes
      </p>
      
      <form @submit.prevent="handleRegisterStep">
        <!-- ÉTAPE 1 : Identifiants -->
        <div v-if="registerStep === 1">
          <div class="form-group">
            <label>Pseudo</label>
            <div class="input-with-counter">
              <input 
                type="text" 
                placeholder="Choisissez un pseudo" 
                v-model="registerPseudo"
                maxlength="12"
                @input="truncatePseudo"
                required
              >
              <span class="char-counter" :class="{ warning: registerPseudo.length >= 10, danger: registerPseudo.length === 12 }">
                {{ registerPseudo.length }}/12
              </span>
            </div>
          </div>
          
          <div class="form-group">
            <label>Email</label>
            <input 
              type="email" 
              placeholder="votre@email.com" 
              v-model="registerEmail" 
              required
            >
          </div>
          
          <div class="form-group">
            <label>Mot de passe</label>
            <input 
              type="password" 
              placeholder="8 caractères minimum" 
              v-model="registerPassword"
              required
              @input="checkPasswordStrength"
            >
            <div v-if="passwordError" class="field-error">{{ passwordError }}</div>
          </div>
        </div>

        <!-- ÉTAPE 2 : Coordonnées + CGU -->
        <div v-else>
          <div class="form-group">
            <label>Numéro de téléphone</label>
            <input 
              type="tel" 
              placeholder="06 XX XX XX" 
              v-model="registerPhone" 
              required
            >
          </div>
          
          <div class="form-group">
            <label>Ville</label>
            <select v-model="registerVille" required>
              <option value="Libreville">Libreville</option>
              <option value="Akanda">Akanda</option>
              <option value="Owendo">Owendo</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>Quartier / Adresse</label>
            <input 
              type="text" 
              placeholder="Ex: Montagne Sainte, PK8..." 
              v-model="registerAdresse" 
              required
            >
          </div>

          <!-- ✅ CASE À COCHER CGU - ÉTAPE 2 -->
          <div class="form-group cgu-group">
            <label class="cgu-checkbox">
              <input 
                type="checkbox" 
                v-model="acceptCgu"
              >
              <span class="checkmark">
                <font-awesome-icon v-if="acceptCgu" icon="check-square" />
                <font-awesome-icon v-else icon="square" />
              </span>
              <span class="cgu-text">
                J'accepte les 
                <router-link to="/conditions-generales" target="_blank" @click.stop>
                  Conditions Générales d'Utilisation
                </router-link>
                et la 
                <router-link to="/politique-confidentialite" target="_blank" @click.stop>
                  Politique de Confidentialité
                </router-link>
              </span>
            </label>
            <p v-if="cguError" class="field-error">{{ cguError }}</p>
          </div>
        </div>

        <div class="form-actions">
          <!-- Étape 1 : Bouton Suivant -->
          <button 
            v-if="registerStep === 1" 
            type="button" 
            class="submit-button next-btn"
            @click="goToStep2"
            :disabled="!isStep1Valid"
          >
            Suivant →
          </button>
          
          <!-- Étape 2 : Boutons -->
          <div v-else class="step2-actions">
            <button type="button" class="back-btn" @click="goToStep1">
              ← Retour
            </button>
            <button type="submit" class="submit-button" :disabled="loading || !acceptCgu">
              {{ loading ? 'Inscription...' : 'S\'inscrire' }}
            </button>
          </div>
        </div>
      </form>
      
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
      
      <p class="auth-switch">
        Déjà un compte ? 
        <a href="#" @click.prevent="switchToLogin">Se connecter</a>
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { useAuth } from './composables/useAuth';

export default defineComponent({
  name: 'AuthModals',
  props: {
    showLoginModal: Boolean,
    showRegisterModal: Boolean
  },
  emits: ['close-modals', 'switch-to-login', 'switch-to-register', 'login-success', 'register-success'],
  setup(props, { emit }) {
    const { login: loginApi, register: registerApi } = useAuth();
    
    // Login
    const loginIdentifier = ref('');
    const loginPassword = ref('');
    
    // Register - Étape 1
    const registerPseudo = ref('');
    const registerEmail = ref('');
    const registerPassword = ref('');
    
    // Register - Étape 2
    const registerPhone = ref('');
    const registerVille = ref('Libreville');
    const registerAdresse = ref('');
    
    // ✅ CGU (à l'étape 2)
    const acceptCgu = ref(false);
    const cguError = ref('');
    
    // États
    const loading = ref(false);
    const errorMessage = ref('');
    const passwordError = ref('');
    const registerStep = ref(1);

    // ✅ Limite du pseudo à 12 caractères
    const truncatePseudo = () => {
      if (registerPseudo.value.length > 12) {
        registerPseudo.value = registerPseudo.value.slice(0, 12);
      }
    };

    // ⚠️ Étape 1 : PAS de CGU ici
    const isStep1Valid = computed(() => {
      return registerPseudo.value.length >= 3 &&
             registerEmail.value.includes('@') &&
             registerPassword.value.length >= 8;
    });

    const checkPasswordStrength = () => {
      if (registerPassword.value.length > 0 && registerPassword.value.length < 8) {
        passwordError.value = 'Le mot de passe doit contenir au moins 8 caractères';
      } else {
        passwordError.value = '';
      }
    };

    const goToStep2 = () => {
      if (isStep1Valid.value) {
        registerStep.value = 2;
        errorMessage.value = '';
      }
    };

    const goToStep1 = () => {
      registerStep.value = 1;
      errorMessage.value = '';
      cguError.value = '';
    };

    const closeModals = () => {
      errorMessage.value = '';
      passwordError.value = '';
      cguError.value = '';
      registerStep.value = 1;
      acceptCgu.value = false;
      emit('close-modals');
    };

    const switchToRegister = () => {
      errorMessage.value = '';
      passwordError.value = '';
      cguError.value = '';
      registerStep.value = 1;
      acceptCgu.value = false;
      emit('switch-to-register');
    };

    const switchToLogin = () => {
      errorMessage.value = '';
      passwordError.value = '';
      cguError.value = '';
      emit('switch-to-login');
    };

    const handleLogin = async () => {
      if (!loginIdentifier.value || !loginPassword.value) {
        errorMessage.value = 'Veuillez remplir tous les champs';
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      const result = await loginApi(loginIdentifier.value, loginPassword.value);
      
      if (result.success) {
        emit('login-success');
        closeModals();
      } else if (result.requiresVerification) {
        errorMessage.value = 'Veuillez vérifier votre email avant de vous connecter.';
        window.location.href = '/verification';
      } else {
        errorMessage.value = result.message || 'Erreur de connexion';
      }
      
      loading.value = false;
    };

    const handleRegisterStep = async () => {
      // Si on est à l'étape 1, passer à l'étape 2
      if (registerStep.value === 1) {
        goToStep2();
        return;
      }

      // ⚠️ Étape 2 : Vérification CGU
      if (!acceptCgu.value) {
        cguError.value = 'Vous devez accepter les CGU pour vous inscrire.';
        return;
      }
      cguError.value = '';

      // Étape 2 : Soumettre l'inscription
      if (!registerPhone.value || !registerAdresse.value) {
        errorMessage.value = 'Veuillez remplir tous les champs';
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      const result = await registerApi({
        pseudo: registerPseudo.value,
        email: registerEmail.value,
        numero_telephone: registerPhone.value,
        ville: registerVille.value,
        adresse: registerAdresse.value,
        password: registerPassword.value,
        password_confirmation: registerPassword.value,
        accept_cgu: acceptCgu.value  // ✅ Envoyer au backend
      });
      
      if (result.success && result.requiresVerification) {
        errorMessage.value = 'Un code de vérification a été envoyé à votre email.';
        window.location.href = '/verification';
      } else if (result.success) {
        emit('register-success');
        closeModals();
      } else {
        errorMessage.value = result.message || 'Erreur d\'inscription';
      }
      
      loading.value = false;
    };

    return {
      // Login
      loginIdentifier,
      loginPassword,
      // Register
      registerPseudo,
      registerEmail,
      registerPhone,
      registerVille,
      registerAdresse,
      registerPassword,
      // CGU
      acceptCgu,
      cguError,
      // États
      loading,
      errorMessage,
      passwordError,
      registerStep,
      isStep1Valid,
      // Méthodes
      truncatePseudo,
      checkPasswordStrength,
      goToStep2,
      goToStep1,
      closeModals,
      switchToRegister,
      switchToLogin,
      handleLogin,
      handleRegisterStep
    };
  }
});
</script>

<style lang="scss" scoped>
@use '@/styles/variables' as *;

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: $blanc;
  padding: 30px;
  border-radius: 10px;
  width: 90%;
  max-width: 400px;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  
  h2 {
    text-align: center;
    margin-bottom: 5px;
    color: $gris-fonce;
  }

  .step-subtitle {
    text-align: center;
    color: $gris-moyen;
    font-size: 0.85rem;
    margin-bottom: 20px;
  }
}

.register-modal {
  max-width: 450px;
}

.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  padding: 0 20px;

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    .step-number {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background: #e9ecef;
      color: $gris-moyen;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 14px;
      transition: all 0.3s;
    }

    .step-label {
      font-size: 11px;
      color: $gris-moyen;
    }

    &.active .step-number {
      background: $orange-doux;
      color: white;
    }

    &.completed .step-number {
      background: #28a745;
      color: white;
    }
  }

  .step-line {
    width: 40px;
    height: 2px;
    background: #e9ecef;
    margin: 0 10px;
    margin-bottom: 18px;
    transition: all 0.3s;

    &.active {
      background: $orange-doux;
    }
  }
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: $gris-moyen;
  
  &:hover {
    color: $gris-fonce;
  }
}

.form-group {
  margin-bottom: 15px;
  
  label {
    display: block;
    font-weight: 500;
    margin-bottom: 5px;
    font-size: 0.85rem;
    color: $gris-fonce;
  }

  input[type="text"],
  input[type="email"],
  input[type="password"],
  input[type="tel"],
  select {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 14px;
    
    &:focus {
      border-color: $orange-doux;
      outline: none;
    }
  }

  select {
    appearance: auto;
    background: white;
  }
  
  &.error input {
    border-color: #dc3545;
  }
}

.input-with-counter {
  position: relative;
  
  input {
    padding-right: 50px;
  }
  
  .char-counter {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
    color: $gris-moyen;
    font-weight: 500;
    transition: color 0.3s;
    
    &.warning {
      color: #f0ad4e;
    }
    
    &.danger {
      color: #dc3545;
      font-weight: 600;
    }
  }
}

.field-error {
  color: #dc3545;
  font-size: 12px;
  margin-top: 5px;
}

// ✅ CASE À COCHER CGU
.cgu-group {
  margin-top: 15px;
  margin-bottom: 10px;
  
  .cgu-checkbox {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
    transition: all 0.2s;
    margin-bottom: 0;
    font-weight: 400;
    font-size: 0.85rem;
    line-height: 1.5;
    
    &:hover {
      background: #f0f0f0;
      border-color: $orange-doux;
    }
    
    input[type="checkbox"] {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      width: 0;
      height: 0;
    }
    
    .checkmark {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      margin-top: 1px;
      
      svg {
        font-size: 20px;
        color: $gris-moyen;
        transition: color 0.2s;
      }
    }
    
    // Quand coché : l'icône check-square devient orange
    input:checked ~ .checkmark svg {
      color: $orange-fonce;
    }
    
    .cgu-text {
      flex: 1;
      color: $gris-fonce;
      
      a {
        color: $orange-fonce;
        text-decoration: none;
        font-weight: 500;
        
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
  
  .field-error {
    margin-top: 6px;
    margin-left: 12px;
  }
}

.forgot-link {
  text-align: right;
  margin-bottom: 15px;
  
  a {
    color: $gris-moyen;
    font-size: 12px;
    text-decoration: none;
    
    &:hover {
      color: $orange-doux;
    }
  }
}

.submit-button {
  width: 100%;
  padding: 12px;
  background-color: $orange-doux;
  color: $blanc;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 5px;
  
  &:hover:not(:disabled) {
    background-color: darken($orange-doux, 10%);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.next-btn {
    margin-top: 10px;
  }
}

.step2-actions {
  display: flex;
  gap: 10px;
  margin-top: 5px;

  .back-btn {
    flex: 1;
    padding: 12px;
    background: #f8f9fa;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #e9ecef;
    }
  }

  .submit-button {
    flex: 2;
    margin-top: 0;
  }
}

.error-message {
  color: #3496d2;
  text-align: center;
  margin-top: 15px;
  font-size: 14px;
}

.auth-switch {
  text-align: center;
  font-size: 14px;
  color: $gris-moyen;
  margin-top: 15px;
  
  a {
    color: $orange-doux;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}
</style>